import React, { FC, useCallback } from 'react'
import { Formik, Form, Field } from 'formik'
import { SavedAddress } from '../apollo/generated/TakeoutWebGraphQLOperations'
import { useApolloClient } from '@apollo/client'
import { useUpdateAddressMutation } from './account-mutations'
import { alertError, alertSuccess } from '../auth/alertUtils'
import { TextInputField, TextareaField } from '@toasttab/buffet-pui-forms'
import { AddressAutocomplete } from '../components/AddressAutocomplete/AddressAutocomplete'
import styles from './account.module.css'
import * as Yup from 'yup'
import { Button } from '@toasttab/buffet-pui-buttons'
import { ButtonVariant } from '@local/do-secundo-button'
import { useRestaurant } from '@local/do-secundo-restaurant-provider/src'
import { useSentry } from 'banquet-runtime-modules'
import { t } from '@local/translations'

interface EditAddressProps {
  address: SavedAddress
  onSuccessfulUpdate: () => void
}

interface EditAddressFormData {
  addressLabel: string
  deliveryInfo: {
    address1: string
    address2?: string
    city: string
    state: string
    zipCode: string
    latitude?: number
    longitude?: number
    country?: string
  }
  notes?: string
}

export const EditAddress: FC<EditAddressProps> = ({
  address,
  onSuccessfulUpdate
}) => {
  const [UpdateAddressMutation] = useUpdateAddressMutation()
  const apolloClient = useApolloClient()
  const { country } = useRestaurant()
  const { captureException } = useSentry()

  // Define the Yup schema
  const validationSchema = Yup.object({
    addressLabel: Yup.string().required(t('edit-address.required')),
    deliveryInfo: Yup.object({
      address1: Yup.string().required(t('edit-address.required')),
      address2: Yup.string().nullable(),
      city: Yup.string().required(t('edit-address.required')),
      state: Yup.string().required(t('edit-address.required')),
      zipCode: Yup.string().required(t('edit-address.required')),
      latitude: Yup.number().required(t('edit-address.required')),
      longitude: Yup.number().required(t('edit-address.required')),
      country: Yup.string().required(t('edit-address.required'))
    }).required(),
    notes: Yup.string().nullable()
  })

  const initialValues: EditAddressFormData = {
    addressLabel: address.name || '',
    deliveryInfo: {
      ...address.deliveryInfo,
      address2: address.deliveryInfo.address2 || '',
      country: address.deliveryInfo.country || country
    },
    notes: address.deliveryInfo.notes || ''
  }

  const handleSubmit = useCallback(
    async (values: EditAddressFormData) => {
      try {
        const { data } = await UpdateAddressMutation({
          variables: {
            input: {
              name: values.addressLabel,
              deliveryInfo: {
                ...values.deliveryInfo,
                latitude: values.deliveryInfo.latitude!!,
                longitude: values.deliveryInfo.longitude!!
              },
              guid: address.guid
            }
          },
          client: apolloClient
        })

        if (!data || data.updateAddress.__typename === 'UpdateAddressError') {
          alertError(t('edit-address.something-went-wrong'))
        } else if (data.updateAddress.__typename === 'UpdateAddressResponse') {
          alertSuccess(t('edit-address.address-updated-successfully'))
          onSuccessfulUpdate()
        }
      } catch (e) {
        alertError(t('edit-address.something-went-wrong'))
        captureException(new Error(`Failed to update address: ${e}`))
      }
    },
    [UpdateAddressMutation, address.guid, onSuccessfulUpdate, apolloClient]
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ isValid, dirty, isSubmitting }) => (
        <div className={styles.editAddressWrapper}>
          <Form>
            <div className={styles.editAddressInputs}>
              <TextInputField
                name='addressLabel'
                label={t('edit-address.address-label')}
                required
                containerClassName='mb-4'
              />

              <Field name='deliveryInfo' component={AddressAutocomplete} />

              <TextareaField
                name='notes'
                label={t('edit-address.delivery-instructions')}
                containerClassName='mb-4'
              />
            </div>

            <div className={'flex space-x-2 justify-end'}>
              <Button
                variant={ButtonVariant.SECONDARY}
                onClick={onSuccessfulUpdate}
                className={'w-fit px-4'}
                type='button'
              >
                {t('edit-address.cancel')}
              </Button>
              <Button
                className={'w-fit px-4'}
                variant={ButtonVariant.PRIMARY}
                type='submit'
                disabled={!(isValid && dirty) || isSubmitting}
              >
                {t('edit-address.update-my-address')}
              </Button>
            </div>
          </Form>
        </div>
      )}
    </Formik>
  )
}
